// THE QUESTION BOX, AND THE QUESTIONS AND ANSWERS PAGE.
//
// Two things that belong together: a reader who cannot find their answer on the page asks it
// through the box, and the box's confirmation points back at the page, because the next reader
// with the same question should find it written down.
//
// WHAT THE BOX IS NOT. It is not live chat. There is no thread, no typing indicator and no
// implication that anyone is at the other end right now: a question goes to Dealquire and the
// reply comes by email. The panel says so in those words.
//
// WHERE A QUESTION GOES. To the `questions` table and to one email address, and nowhere else.
// NOTHING SUBMITTED THROUGH THE BOX IS EVER DISPLAYED ANYWHERE ON THE SITE - no route reads a
// question back out, and the Questions and Answers page below is a hand-written file on the
// server (backend/qa_content.py), never a feed of what people have asked.
//
// THE CONTEXT IS ATTACHED, AND SHOWN. A question like "why is this n.a.?" cannot be answered
// without knowing which figure on which deal, and asking a follow-up by email costs a day. So
// the page or tab and the deal on screen ride along with the question - and the panel lists
// exactly what is going, because attaching something a user cannot see is not something to do
// quietly.
//
// There is no router here either: QUESTIONS_PATH is a view swap through the same pushState
// pattern the methodology page uses, and both hosts rewrite every path to index.html.
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { C, F, S, W, SP, R, BW } from './tokens';
import { navigate, usePath, METHODOLOGY_PATH } from './Explain';
import { useRouteHead } from './head';

export const QUESTIONS_PATH = '/questions';
const MAX_CHARS = 2000;          // mirrors QUESTION_MAX_CHARS in app.py
const PER_HOUR = 5;              // mirrors QUESTION_RATE_LIMIT in app.py

// ---- WHAT IS ON SCREEN --------------------------------------------------------------------
// The box sits outside the page it is asking about, so the page tells it what it is looking at.
// One provider at the root, one publisher call from the deal app; a page that publishes nothing
// still gets its name, because the box derives that from the path.
const QuestionContext = createContext({ info: {}, publish: () => {} });

function sameInfo(a, b) {
  const ka = Object.keys(a || {}), kb = Object.keys(b || {});
  return ka.length === kb.length && ka.every(k => a[k] === b[k]);
}

export function QuestionContextProvider({ children }) {
  const [info, setInfo] = useState({});
  // Stable, and a no-op when nothing changed, so an effect that publishes on every render
  // cannot put the app into a loop.
  const publish = useCallback((next) => {
    setInfo(prev => (sameInfo(prev, next) ? prev : next));
  }, []);
  return (
    <QuestionContext.Provider value={{ info, publish }}>{children}</QuestionContext.Provider>
  );
}

export function useQuestionPublisher() {
  return useContext(QuestionContext).publish;
}

// ---- THE BOX ------------------------------------------------------------------------------
const BTN_SIZE = 52;

export function QuestionBox({ apiBase, productName }) {
  const { info } = useContext(QuestionContext);
  const path = usePath();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [sentTo, setSentTo] = useState(null);
  // The user comes from the deal app where there is one. On the methodology and Questions and
  // Answers pages nothing publishes it, so the box asks the server itself - once, when it is
  // first opened, rather than on every page load.
  const [fetchedUser, setFetchedUser] = useState(undefined);
  const asked = useRef(false);
  const user = info.user !== undefined ? info.user : fetchedUser;

  useEffect(() => {
    if (!open || info.user !== undefined || asked.current) return;
    asked.current = true;
    fetch(`${apiBase}/api/auth/me`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setFetchedUser(d.user || null))
      .catch(() => setFetchedUser(null));
  }, [open, info.user, apiBase]);

  // Escape closes the panel, which is what a reader expects of anything that floats over a page.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const pageName = path === METHODOLOGY_PATH ? 'Methodology'
                 : path === QUESTIONS_PATH ? 'Questions and Answers'
                 : (info.page || 'Home');
  // The deal belongs to the deal app's page. Kept off every other path so a question asked from
  // the methodology page never carries the deal a reader had open before they navigated.
  const onDealPage = path !== METHODOLOGY_PATH && path !== QUESTIONS_PATH;
  const context = {
    page: pageName,
    tab: onDealPage ? (info.tab || null) : null,
    ticker1: onDealPage ? (info.ticker1 || null) : null,
    ticker2: onDealPage ? (info.ticker2 || null) : null,
    ownership_pct: onDealPage ? info.ownership_pct : null,
    premium_pct: onDealPage ? info.premium_pct : null,
    cash_pct: onDealPage ? info.cash_pct : null,
  };
  const attached = [
    ['Page', context.page],
    ['Tab', context.tab],
    ['Acquirer', context.ticker1],
    ['Target', context.ticker2],
    ['Ownership', context.ownership_pct == null ? null : `${context.ownership_pct}%`],
    ['Offer premium', context.premium_pct == null ? null : `${context.premium_pct}%`],
    ['Cash share of the consideration',
     context.cash_pct == null ? null : `${context.cash_pct}%`],
  ].filter(([, v]) => v !== null && v !== undefined && v !== '');

  const left = MAX_CHARS - text.length;
  const send = async () => {
    if (busy) return;
    const body = text.trim();
    if (!body) { setErr('Please type your question first.'); return; }
    if (body.length > MAX_CHARS) {
      setErr(`A question can be up to ${MAX_CHARS.toLocaleString('en-IN')} characters.`);
      return;
    }
    setBusy(true); setErr('');
    try {
      const res = await fetch(`${apiBase}/api/question`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ question: body, context }),
      });
      if (res.status === 429) {
        // The limiter answers before the route runs, so this is the only place the per-hour
        // limit can be explained.
        setErr(`That is ${PER_HOUR} questions in an hour, which is the limit. `
               + 'Please send the rest a little later.');
        setBusy(false);
        return;
      }
      let data = {};
      try { data = await res.json(); } catch (e) { /* a non-JSON error page */ }
      if (!res.ok) {
        setErr(data.message || "Your question couldn't be sent just now. Please try again.");
        setBusy(false);
        return;
      }
      setSentTo(data.reply_to || (user && user.email) || null);
      setText('');
    } catch (e) {
      setErr("Your question couldn't be sent just now. Please check your connection and try again.");
    }
    setBusy(false);
  };

  const panelStyle = {
    position: 'fixed', zIndex: 200,
    right: SP.lg, bottom: `calc(${BTN_SIZE}px + ${SP.xl})`,
    width: `min(24rem, calc(100vw - ${SP.xl} - ${SP.xl}))`,
    maxHeight: 'min(34rem, calc(100vh - 7rem))', overflowY: 'auto',
    backgroundColor: C.surface, border: `${BW.hair} solid ${C.line}`, borderRadius: R.md,
    padding: `${SP.lg} ${SP.lg}`, color: C.text, fontFamily: F.ui,
    boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
  };
  const labelStyle = { color: C.textLabel, fontSize: S.micro, fontWeight: W.semi,
                       letterSpacing: '0.06em', margin: `0 0 ${SP.xs}` };
  const noteStyle = { color: C.textFaint, fontSize: S.small, lineHeight: 1.5, margin: 0 };

  return (
    <>
      <button type="button" onClick={() => { setOpen(o => !o); setErr(''); }}
              aria-label={open ? 'Close the question box' : 'Ask a question'}
              style={{ position: 'fixed', right: SP.lg, bottom: SP.lg, zIndex: 201,
                       width: `${BTN_SIZE}px`, height: `${BTN_SIZE}px`, borderRadius: R.pill,
                       backgroundColor: C.accent, color: C.bg, border: 'none',
                       fontFamily: F.ui, fontSize: S.lede, fontWeight: W.bold,
                       cursor: 'pointer', boxShadow: '0 10px 28px rgba(0,0,0,0.55)',
                       // A tap target, not a hover target: 52px square clears the 44px
                       // minimum, and touchAction stops a tap being read as a double-tap zoom.
                       touchAction: 'manipulation', lineHeight: 1 }}>
        {open ? '×' : '?'}
      </button>
      {open && (
        <div style={panelStyle} role="dialog" aria-label="Ask a question">
          {sentTo ? (
            <>
              <p style={{ color: C.text, fontSize: S.h3, fontWeight: W.semi,
                          margin: `0 0 ${SP.sm}` }}>Your question has been received</p>
              <p style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.55,
                          margin: `0 0 ${SP.md}` }}>
                A reply will come by email to {sentTo}. It may already be answered on the
                Questions and Answers page.
              </p>
              <a href={QUESTIONS_PATH}
                 onClick={e => { e.preventDefault(); setOpen(false); navigate(QUESTIONS_PATH); }}
                 style={{ color: C.accent, fontSize: S.body, fontWeight: W.semi,
                          textDecoration: 'none' }}>
                Read the Questions and Answers page
              </a>
              <p style={{ ...noteStyle, marginTop: SP.md }}>
                <span onClick={() => { setSentTo(null); setErr(''); }}
                      style={{ color: C.textMuted, cursor: 'pointer' }}>Ask another question</span>
              </p>
            </>
          ) : (
            <>
              <p style={{ color: C.text, fontSize: S.h3, fontWeight: W.semi,
                          margin: `0 0 ${SP.xs}` }}>Ask a question</p>
              <p style={{ color: C.textMuted, fontSize: S.small, lineHeight: 1.5,
                          margin: `0 0 ${SP.md}` }}>
                Your question goes to {productName} and the reply comes by email. This is not a
                chat, so nobody is typing at the other end.
              </p>
              {!user ? (
                <>
                  <p style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.55,
                              margin: `0 0 ${SP.md}` }}>
                    Please sign in first, so the reply has an address to go to.
                  </p>
                  {info.onSignIn ? (
                    <button type="button"
                            onClick={() => { setOpen(false); info.onSignIn(); }}
                            style={{ width: '100%', padding: '11px', backgroundColor: C.accent,
                                     color: C.bg, border: 'none', borderRadius: R.sm,
                                     fontSize: S.body, fontWeight: W.semi, fontFamily: F.ui,
                                     cursor: 'pointer' }}>
                      Sign in
                    </button>
                  ) : (
                    // The methodology and Questions and Answers pages carry no sign-in panel,
                    // so the box sends the reader to the page that does rather than pretending.
                    <a href="/" onClick={e => { e.preventDefault(); setOpen(false); navigate('/'); }}
                       style={{ color: C.accent, fontSize: S.body, fontWeight: W.semi,
                                textDecoration: 'none' }}>
                      Sign in on the main page
                    </a>
                  )}
                </>
              ) : (
                <>
                  <p style={labelStyle}>THE REPLY WILL GO TO</p>
                  <p style={{ color: C.text, fontSize: S.body, margin: `0 0 ${SP.xs}`,
                              wordBreak: 'break-all' }}>{user.email}</p>
                  <p style={{ ...noteStyle, margin: `0 0 ${SP.md}` }}>
                    This is your account email. Check it reads correctly, because it is the
                    address the answer goes to.
                  </p>
                  <p style={labelStyle}>WHAT IS ATTACHED</p>
                  <div style={{ border: `${BW.hair} solid ${C.line}`, borderRadius: R.sm,
                                padding: `${SP.sm} ${SP.md}`, marginBottom: SP.md,
                                backgroundColor: C.surfaceAlt }}>
                    {attached.map(([label, value]) => (
                      <p key={label} style={{ display: 'flex', justifyContent: 'space-between',
                                              gap: SP.md, margin: `${SP.xxs} 0`,
                                              fontSize: S.small, color: C.textMuted }}>
                        <span>{label}</span>
                        <span style={{ color: C.text, textAlign: 'right' }}>{value}</span>
                      </p>
                    ))}
                    <p style={{ ...noteStyle, marginTop: SP.xs }}>
                      Sent with your question so it can be answered without a follow-up.
                    </p>
                  </div>
                  <textarea value={text} maxLength={MAX_CHARS}
                            onChange={e => { setText(e.target.value); if (err) setErr(''); }}
                            placeholder="What would you like to know?"
                            rows={5}
                            style={{ width: '100%', boxSizing: 'border-box', resize: 'vertical',
                                     backgroundColor: C.raised, color: C.text,
                                     border: `${BW.hair} solid ${C.line}`, borderRadius: R.sm,
                                     padding: `${SP.sm} ${SP.md}`, fontFamily: F.ui,
                                     fontSize: S.body, lineHeight: 1.5, outline: 'none' }} />
                  <p style={{ ...noteStyle, margin: `${SP.xs} 0 ${SP.md}` }}>
                    {left.toLocaleString('en-IN')} of {MAX_CHARS.toLocaleString('en-IN')}
                    {' '}characters left. You can ask up to {PER_HOUR} questions an hour.
                  </p>
                  {err && (
                    <p style={{ color: C.negative, fontSize: S.small, lineHeight: 1.5,
                                margin: `0 0 ${SP.sm}` }}>{err}</p>
                  )}
                  <button type="button" onClick={send} disabled={busy}
                          style={{ width: '100%', padding: '11px',
                                   backgroundColor: busy ? C.textFaint : C.accent, color: C.bg,
                                   border: 'none', borderRadius: R.sm, fontSize: S.body,
                                   fontWeight: W.semi, fontFamily: F.ui,
                                   cursor: busy ? 'not-allowed' : 'pointer',
                                   touchAction: 'manipulation' }}>
                    {busy ? 'Sending…' : 'Send question'}
                  </button>
                  <p style={{ ...noteStyle, marginTop: SP.md }}>
                    Your question and your account email are sent to {productName} so a reply can
                    be made. Nothing else is done with them, and nothing you send here is shown
                    anywhere on the site.
                  </p>
                </>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

// ---- THE PAGE -----------------------------------------------------------------------------
// The six answers are written in backend/qa_content.py and fetched from /api/qa, for the same
// reason every explanation lives in explanations.py: one place to edit, no second copy to drift.
export function QuestionsAnswersPage({ apiBase, productName }) {
  const [state, setState] = useState({ status: 'loading', title: 'Questions and Answers',
                                       entries: [] });
  useEffect(() => {
    let live = true;
    fetch(`${apiBase}/api/qa`)
      .then(r => r.json())
      .then(d => { if (live) setState({ status: 'ready', title: d.title || 'Questions and Answers',
                                        entries: d.entries || [] }); })
      .catch(() => { if (live) setState(s => ({ ...s, status: 'error' })); });
    return () => { live = false; };
  }, [apiBase]);

  // G2. See head.js: one helper for every route's title, description and canonical.
  useRouteHead({
    title: `Questions and Answers | ${productName}`,
    description: 'Answers to the questions readers ask about Dealquire: where the figures '
      + 'come from, what the models assume, and what the site cannot tell you.',
    path: QUESTIONS_PATH,
  });

  // A direct load of /questions#why-na arrives before the content does, so the browser's own
  // jump finds nothing. Jump once the sections exist - the same fix the methodology page needs.
  useEffect(() => {
    if (state.status !== 'ready' || !window.location.hash) return;
    const el = document.getElementById(window.location.hash.slice(1));
    if (el) el.scrollIntoView();
  }, [state.status]);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: C.bg, color: C.text, fontFamily: F.ui,
                  padding: `${SP.xl} ${SP.lg} ${SP.max}` }}>
      <div style={{ maxWidth: '48rem', margin: '0 auto' }}>
        <div style={{ display: 'flex', gap: SP.lg, flexWrap: 'wrap' }}>
          <a href="/" onClick={e => { e.preventDefault(); navigate('/'); }}
             style={{ color: C.accent, fontSize: S.small, fontWeight: W.semi,
                      textDecoration: 'none' }}>
            Back to {productName}
          </a>
          <a href={METHODOLOGY_PATH} style={{ color: C.textMuted, fontSize: S.small,
                                              fontWeight: W.semi, textDecoration: 'none' }}>
            Methodology
          </a>
        </div>
        <h1 style={{ fontFamily: F.display, fontWeight: W.normal, fontSize: S.display,
                     margin: `${SP.lg} 0 ${SP.sm}`, lineHeight: 1.1 }}>{state.title}</h1>
        <p style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.6,
                    margin: `0 0 ${SP.xxl}` }}>
          What {productName} is, where its figures come from, and what it will not tell you.
        </p>

        {state.status === 'loading' && (
          <p style={{ color: C.textMuted, fontSize: S.body }}>Loading…</p>
        )}
        {state.status === 'error' && (
          <p style={{ color: C.negative, fontSize: S.body }}>
            The answers couldn't be loaded. Check that the server is running and reload.
          </p>
        )}
        {state.status === 'ready' && state.entries.map(entry => (
          <section key={entry.id} id={entry.id}
                   style={{ marginBottom: SP.xxl, scrollMarginTop: SP.xl }}>
            <h2 style={{ fontSize: S.h2, fontWeight: W.semi, lineHeight: 1.3,
                         margin: `0 0 ${SP.md}` }}>{entry.question}</h2>
            {entry.answer.map((para, i) => (
              <p key={i} style={{ color: C.textMuted, fontSize: S.body, lineHeight: 1.7,
                                  margin: `0 0 ${SP.md}` }}>{para}</p>
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}
